// @flow
import * as React from 'react';
import type { SwitchDataPort } from 'esm-web-pro/types/networks';
import ContentRow from 'esm-web-pro/components/layout/sidePanel/ContentRow';
import RowActions from 'esm-web-pro/components/layout/sidePanel/RowActions';
import MeatballsIcon from 'esm-web-pro/components/layout/sidePanel/icons/MeatballsIcon';
import actionMessages from 'esm-web-pro/messages/actionMessages';
import { withRouter } from 'react-router';
import {
    defineMessages, injectIntl, IntlShape,
} from 'react-intl';
import dataPortMessages from 'esm-web-pro/messages/dataPortMessages';
import { Box } from 'grommet';
import { POINT12_CONTENT_TOOLTIP_KEY }
    from 'esm-web-pro/containers/pages/network/objects/point12/CableList/Point12CableList';
import SFPRow from 'esm-web-pro/containers/pages/network/objects/point12/poplayer12/SFPRow';
import { ClickedRowContext } from 'esm-web-pro/containers/pages/network/objects/point12/ClickedRowContext';
import { connectAPI } from 'speed-js-react';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import type { Map } from 'esm-web-pro/containers/map/types';
import { highlightFiberCircuit } from 'esm-web-pro/containers/pages/network/highlight';
import useHighlightFiber from 'esm-web-pro/containers/pages/network/objects/fiber/useHighlightFiber';
import { EditorContext } from 'esm-web-pro/containers/map/editor/BaseEditor';
import { useLeaflet } from 'react-leaflet';
import { isEmpty } from 'lodash';
import ValueText from 'esm-web-pro/components/layout/sidePanel/ValueText';


const msg = defineMessages({
    dataCircuit: {
        id: 'data-circuit',
        defineMessages: 'Data Circuit',
    },
});

type Props = {
    point: Object,
    port: SwitchDataPort,
    onRemoveSFP: Function,
    onPortConnect: Function,
    onPortDisconnect: Function,

    history: History,
    match: Match,
    intl: IntlShape,
    onAddSFP: Function,
};

const DataPortRow = (props: Props) => {
    const {
        port, point,
        history, match, intl: { formatMessage }, onAddSFP, onRemoveSFP, onPortConnect, onPortDisconnect,
    } = props;

    let pointCoords = point.container.coordinates.coordinates;
    pointCoords = [pointCoords[1], pointCoords[0]];
    const [clickedRowIdentity, setClickedRowIdentity] = React.useContext(ClickedRowContext);
    const [highlightFiberFunction] = useHighlightFiber();
    const editor = React.useContext(EditorContext);
    const leaflet = useLeaflet();

    const rowId = `PORT-${port.id}`;
    const sfpRowId = port.sfp ? `PORT-${port.sfp.uuid}` : null;

    const actions = [
        {
            key: `${port.uuid}-more`,
            icon: <MeatballsIcon />,
            size: '20px',
            width: '3px',
            callback: (e: SyntheticMouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                const path = match.url.split('/layer');
                history.push(`${path[0]}/layer/dataport/${port.identity}`);
            },
            tooltip: formatMessage(actionMessages.more),
            tooltipKey: POINT12_CONTENT_TOOLTIP_KEY,
        },
    ];

    const handleAddSFP = () => {
        onAddSFP(port);
    };

    const handleClick = (clickedRowId) => {
        editor.unregisterAllLoadingCallback();
        setClickedRowIdentity(clickedRowId);

        const allCoordinates = [];
        port.sfp.optical_switch_ports.forEach((opticalSwitchPort) => {
            if (opticalSwitchPort.port && opticalSwitchPort.port.fiber) {
                const [callbacks, circuitPath] = highlightFiberCircuit(
                    opticalSwitchPort.circuit_path.path, opticalSwitchPort.port.fiber, leaflet.map,
                    highlightFiberFunction, pointCoords,
                );
                allCoordinates.push(...circuitPath);
                editor.registerMultipleLoadingCallbacks(callbacks);
                editor.reloadFeatures();
            }
        });

        if (!isEmpty(allCoordinates)) {
            leaflet.map.highlightedGeometry = {
                bounds: allCoordinates, center: pointCoords,
            };
            leaflet.map.flyToBoundsCenter(allCoordinates, pointCoords);
        } else {
            leaflet.map.highlightedGeometry = {
                bounds: null, center: pointCoords,
            };
            leaflet.map.panTo(pointCoords);
        }
    };

    const rowProps = {};
    if (port.sfp?.optical_switch_ports.find((opticalSwitchPort) => opticalSwitchPort.port?.fiber)) {
        rowProps.onClick = () => handleClick(rowId);
    }

    return (
        <Box pad={{ bottom: 'xsmall' }}>
            <ContentRow
                padLevel={1}
                rowId={rowId}
                clickedRowIdentity={clickedRowIdentity}
                actions={(
                    <RowActions
                        iconPadding="3px"
                        gapBetweenActions="small"
                        actions={actions}
                    />
                )}
                {...rowProps}
            >
                <ValueText>
                    {`${formatMessage(dataPortMessages.dataport)} ${port.data_port_number} 
                    ${port.code ? `(${port.code})` : ''}`}
                    {port.data_circuit && ` - ${formatMessage(msg.dataCircuit)} ${port.data_circuit.ref}`}
                </ValueText>
            </ContentRow>
            <SFPRow
                SFP={port.sfp}
                rowId={sfpRowId}
                pointCoords={pointCoords}
                onAddSFP={handleAddSFP}
                onRemoveSFP={onRemoveSFP}
                onPortConnect={onPortConnect}
                onPortDisconnect={onPortDisconnect}
                onVisualise={handleClick}
                tooltipKey={POINT12_CONTENT_TOOLTIP_KEY}
                padLevel={2}
                portPadLevel={3}
            />
        </Box>
    );
};

const apiCallback = (apiClient: ApiClient) => ({
    createSFP: {
        send: (uuid: string, map: Map) => {
            if (map.is_draft) {
                return apiClient.resources.fiber.circuit(uuid, { is_draft: map.uuid });
            }
            return apiClient.resources.fiber.circuit(uuid);
        },
    },
});

export default withRouter(injectIntl(connectAPI(apiCallback)(DataPortRow)));
